import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { ContactForm, BulkForm } from "@/components/forms";
import { useSite } from "@/context/SiteContext";
import { telLink, waLink, mailLink } from "@/lib/api";
import { Phone, Mail, MapPin, MessageCircle, Tractor } from "lucide-react";

export default function Contact() {
  const { settings } = useSite();
  const showAddress = settings.address && !settings.address.startsWith("[INSERT");

  const cards = [
    { icon: Phone, title: "Call Us", value: settings.phone, href: telLink(settings), testid: "contact-call-card" },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      value: settings.phone,
      href: waLink(settings, "Hello ICROPSIA team, I would like to know more about your seeds."),
      testid: "contact-whatsapp-card",
      external: true,
    },
    { icon: Mail, title: "Email", value: settings.email, href: mailLink(settings), testid: "contact-email-card" },
  ];

  return (
    <>
      <Seo
        title="Contact ICROPSIA India — Seed Enquiries & Bulk Supply"
        description="Reach ICROPSIA India for product information, retail stocking and bulk seed supply enquiries across Bihar. Call, WhatsApp or send an enquiry."
      />
      <section className="pt-40 pb-16 bg-brand-ink relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-site relative max-w-3xl">
          <Reveal>
            <Eyebrow light>Contact</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Let's <span className="font-editorial italic font-semibold text-brand-wheat">Grow Together</span>
            </h1>
            <p className="mt-5 text-white/70 leading-relaxed">
              Whether you are a farmer looking for product information, a retailer interested in stocking ICROPSIA
              seeds, or a distributor exploring a business partnership, our team is ready to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-site grid sm:grid-cols-3 gap-5">
          {cards.map(({ icon: Icon, title, value, href, testid, external }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <a
                href={href}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={testid}
                className="block h-full rounded-2xl border border-brand-deep/10 bg-white p-6 hover:border-brand-gold/60 hover:shadow-lg transition-all group"
              >
                <span className="w-11 h-11 rounded-xl bg-brand-deep flex items-center justify-center mb-4 group-hover:bg-brand-gold transition-colors">
                  <Icon className="w-5 h-5 text-brand-wheat group-hover:text-brand-deep transition-colors" />
                </span>
                <h2 className="font-display font-bold text-brand-deep">{title}</h2>
                <p className="mt-1 text-sm text-brand-charcoal/60">{value}</p>
              </a>
            </Reveal>
          ))}
        </div>
        <div className="container-site mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Reveal delay={0.06}>
            <div className="h-full rounded-2xl border border-brand-deep/10 bg-brand-beige/60 p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-brand-gold mt-1 shrink-0" />
              <div>
                <h2 className="font-display font-bold text-brand-deep">Registered Office</h2>
                <p className="mt-1 text-sm text-brand-charcoal/60">
                  {showAddress ? settings.address : "Registered office address will be published after verification."}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl border border-brand-deep/10 bg-brand-beige/60 p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-brand-gold mt-1 shrink-0" />
              <div>
                <h2 className="font-display font-bold text-brand-deep">Regional Office — Bihar</h2>
                <p className="mt-1 text-sm text-brand-charcoal/60">
                  {settings.regional_address || "Regional office details will be published after verification."}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="h-full rounded-2xl border border-brand-deep/10 bg-brand-beige/60 p-6 flex items-start gap-4">
              <Tractor className="w-5 h-5 text-brand-gold mt-1 shrink-0" />
              <div>
                <h2 className="font-display font-bold text-brand-deep">Regional Operations</h2>
                <p className="mt-1 text-sm text-brand-charcoal/60">
                  {settings.region_text || "Serving farmers, dealers and agricultural businesses across Bihar."}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="container-site grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <Eyebrow>Write to Us</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-brand-deep">Send Us a Message</h2>
            <p className="mt-3 text-sm text-brand-charcoal/60 leading-relaxed">
              Share your question and our team will respond. For urgent queries, call or WhatsApp us directly.
            </p>
            <div className="mt-8 bg-white rounded-3xl border border-brand-deep/10 p-6 sm:p-8 shadow-xl shadow-brand-deep/5">
              <ContactForm />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-3xl bg-brand-deep text-white p-7 sm:p-9 relative overflow-hidden" id="bulk">
              <div className="absolute inset-0 grain opacity-20" />
              <div className="relative">
                <Eyebrow light>Bulk Orders</Eyebrow>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Looking for Bulk Seed Supply?</h2>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  Dealers, distributors and institutional buyers — tell us your requirement and we'll respond with
                  availability.
                </p>
                <div className="mt-7 bg-brand-ivory text-brand-charcoal rounded-2xl p-5 sm:p-6">
                  <BulkForm />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
